/**
 * Where a blow came from, and what that entitles it to set off.
 *
 * ## Why an origin exists before a single jewel does
 *
 * Because the whole anti-feedback rule of
 * `docs/gates/v28d2-jewel-mechanics-contract.md` is a statement about origins:
 *
 * > Nenhum gatilho aceita origem `jewel`.
 *
 * That sentence is worth more than a depth counter, and it is worth more than
 * a comment. A counter has to be threaded through every effect and decremented
 * correctly; a comment has to be remembered. A closed table has neither
 * failure mode: the recursion depth of a jewel effect is exactly one because
 * there is no edge in the graph that would give it a second.
 *
 * Introducing it here, with no jewel to exercise it, is the point. Adding the
 * origin alongside the first proc would mean one change that both classified
 * the damage path and gave it new behaviour, with nothing to compare against.
 *
 * ## Never inferred from a value
 *
 * A blow is not classified by how much it is worth, by which entity produced
 * it, or by whether a projectile id happens to be present. Every call site
 * NAMES its origin, and there is no default parameter anywhere - a default is
 * exactly the mechanism by which a new call site inherits a classification
 * nobody chose for it. The hunt's `applyHit` used to take `fromPet = false`,
 * and replacing that default is half of this gate.
 */

/* -------------------------------------------------------------------------- */
/*  The origins                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Every origin the two simulations know.
 *
 * - `cannon` - a ship's own gun, in either mode. The only origin that will ever
 *   be allowed to set off a jewel.
 * - `pet` - a combat pet's shot, beam or splash. The hunt has pets; **the duel
 *   does not and will not**, which {@link PvpDamageOrigin} states in the type
 *   rather than in a rule somebody has to keep.
 * - `jewel` - anything a jewel emits: a missile, a cyclone cut, an atomic blast,
 *   a bleed detonation, a poison tick, a reflection.
 * - `saw` - **an orbiting saw of the Serrated Diamond touching an enemy**
 *   (gate V28G.P). A jewel emits it, and it is deliberately NOT `jewel`: the
 *   contract makes a confirmed saw contact able to set off Poison, Bleeding, the
 *   Cyclone and Lifesteal, which the `jewel` row forbids to everything. It is a
 *   fourth ROW rather than a widening of the third, so the two are told apart by
 *   the table instead of by a condition somebody has to remember.
 * - `temporary_cannon` - **o disparo PRIMÁRIO de um armamento temporário que a
 *   própria nave pilota** (gate V28H.H). Hoje: os dois canhões dos Super
 *   Canhões.
 *
 *   Uma quinta LINHA, estreita de propósito, e nunca uma permissão genérica
 *   para "carta" ou para "summon". O contrato é explícito: só os projéteis
 *   primários desta habilidade podem iniciar a avaliação de joias, e o dano
 *   ESPECIAL de uma carta - um estouro, uma colisão, um corpo - não ganha essa
 *   permissão por vir da mesma carta. Uma origem `card` genérica seria
 *   exactamente essa permissão.
 *
 *   Porque não reutilizar `cannon`: porque as duas têm de poder divergir. O
 *   canhão comum é a nave; este é um armamento que existe por seis segundos e
 *   que um gate futuro pode querer tratar de outra maneira - e nesse dia a
 *   diferença tem de ser uma linha da tabela, e não uma condição espalhada.
 */
export const DAMAGE_ORIGINS = [
  'cannon',
  'pet',
  'jewel',
  'saw',
  'temporary_cannon',
  /**
   * **O dano ESPECIAL de uma carta** (gate V28H.L).
   *
   * A lâmina do Turbilhão, a explosão da Esfera Ígnea, a do Catolé, cada parcela
   * do feixe da Lança Solar, e a colisão kamikaze de um Drone.
   *
   * **NÃO** o disparo primário de uma invocação: esse é armamento que alguém
   * pilota, e o contrato do catálogo (gate V28H.B) é explícito - "os ataques
   * primários dessas entidades podem iniciar a loteria de joias". Um tiro do
   * Drone ou de um canhão do Leviatã nasce `temporary_cannon`, como os Super
   * Canhões, e activa as joias de quem o invocou ou o pilota.
   */
  'card',
] as const;

export type DamageOrigin = (typeof DAMAGE_ORIGINS)[number];

/**
 * The origins a DUEL can produce.
 *
 * `pet` is excluded at the type, so a call site inside `src/pvp/` cannot pass
 * one and a reviewer does not have to notice. Adding a pet to the duel would be
 * adding a combatant, not widening a label - and it would have to happen here,
 * visibly, rather than by somebody passing a string that compiled.
 *
 * ## `temporary_cannon` entrou, e a razão é a mesma pela qual saíra
 *
 * Ela esteve excluída enquanto os Super Canhões eram só da caça (gate V28H.H):
 * o duelo não os tinha, e um tipo que os admitisse seria uma promessa por
 * escrever. O gate V28H.J deu-lhos, portanto a exclusão passou a descrever uma
 * coisa falsa - e uma sala que não pudesse nomear a origem dos seus próprios
 * tiros teria de os disfarçar de `cannon`, que é exatamente o que a tabela
 * fechada existe para impedir.
 *
 * `pet` continua fora, e continua a ser a mesma frase: o duelo não tem pets e
 * não vai ter.
 */
export type PvpDamageOrigin = Exclude<DamageOrigin, 'pet'>;

/* -------------------------------------------------------------------------- */
/*  What may set off a jewel                                                  */
/* -------------------------------------------------------------------------- */

/**
 * **The closed table.** One row per origin, no default, no fallthrough.
 *
 * `Record<DamageOrigin, boolean>` is what makes it closed: adding a fourth
 * origin without deciding its trigger rights is a compile error, not an
 * omission that defaults to permissive.
 *
 * | origin | may trigger a jewel | why |
 * |---|---|---|
 * | `cannon` | **true** | every proc in the contract is "chance per shot" or "per hit", and a shot is a cannon |
 * | `pet` | false | a pet is not the player's gun. Letting it proc would multiply every chance by however many pets exist, and the contract prices chances against one ship's cadence |
 * | `jewel` | false | the anti-feedback rule, and the reason the table exists |
 * | `saw` | **true** | the V28G.P contract says so outright, and {@link DAMAGE_ORIGIN_TRIGGERABLE_EFFECTS} says WHICH ones |
 *
 * ## Why `saw` is true, and why that does not reopen the loop
 *
 * Because this boolean is now only half the answer. It says a blow may set off
 * SOMETHING; the second table says what. A saw contact may seed Poison, seed a
 * Bleeding mark, cut with the Cyclone and siphon with Lifesteal - and it may not
 * launch a Missile or drop an Atomic Core, which are the two the owner excluded
 * by name. What a saw may set off is a strict subset of what a cannon may.
 *
 * The recursion is still finite, and still by construction rather than by a
 * counter: everything a saw sets off is emitted with origin `jewel`, and the
 * `jewel` row is false. Depth is two here where it was one - shot to saw is not
 * an edge, because a saw is not fired - and there is no third step.
 */
export const DAMAGE_ORIGIN_MAY_TRIGGER_JEWEL: Readonly<Record<DamageOrigin, boolean>> =
  Object.freeze({
    cannon: true,
    pet: false,
    jewel: false,
    saw: true,
    /*
     * O disparo primário de um armamento temporário SIM (gate V28H.H).
     *
     * É a metade positiva do contrato: os dois projéteis dos Super Canhões são
     * armamento que a própria nave pilota, e as joias ofensivas do dono contam
     * neles. A metade negativa está na tabela seguinte e no facto de tudo o que
     * uma joia emitir continuar a nascer com origem `jewel`, que não origina
     * nada.
     */
    temporary_cannon: true,
    /*
     * **O dano especial de uma carta NÃO** (gate V28H.L).
     *
     * A metade negativa do mesmo contrato do catálogo, escrita na tabela em vez
     * de num `if`: "o corpo, a explosão, a colisão kamikaze e o dano especial
     * de uma carta não ativam joias, salvo decisão explícita posterior".
     *
     * É também o que fecha a recursão sem um contador: uma lâmina do Turbilhão
     * não sorteia uma Bomba, uma Bomba de carta não sorteia um corte, e não há
     * um terceiro passo por onde a coisa pudesse continuar.
     */
    card: false,
  });

/** Reads the table. The only way the question is ever asked. */
export function damageOriginMayTriggerJewel(origin: DamageOrigin): boolean {
  return DAMAGE_ORIGIN_MAY_TRIGGER_JEWEL[origin];
}

/* -------------------------------------------------------------------------- */
/*  O que pode ferir um RECURSO                                               */
/* -------------------------------------------------------------------------- */

/**
 * **A segunda tabela fechada: que origens magoam uma coisa inerte.**
 *
 * Um recurso do mundo aberto — hoje um cometa — é rocha. Não tem vida no
 * sentido em que uma nave tem: não se cura, não se envenena, não sangra, não
 * reflete e não alimenta roubo de vida. Perguntar "quanto dano leva" a uma
 * pedra é uma pergunta diferente de a fazer a um piloto, e por isso é uma
 * tabela diferente em vez de uma reutilização da anterior.
 *
 * `Record<DamageOrigin, boolean>` outra vez, e pela mesma razão: uma origem
 * nova sem decisão é um erro de compilação, e **não** existe `default`
 * permissivo. Uma origem que ninguém classificou não parte pedra por omissão.
 *
 * | origem | fere um recurso | porquê |
 * |---|---|---|
 * | `cannon` | **sim** | é o tiro, e é com ele que um cometa se parte |
 * | `temporary_cannon` | **sim** | armamento que a nave pilota; a rocha não distingue |
 * | `card` | **sim** | a Bomba Atómica parte várias partes de uma vez, por contrato |
 * | `jewel` | **sim** | mísseis e núcleos atómicos são carga física a bater em pedra |
 * | `saw` | **sim** | uma lâmina a raspar rocha corta rocha |
 * | `pet` | não | não há pets num quadrante, e o dano de um pet é preçado contra naves |
 *
 * ## Isto é permissão, não é encaminhamento
 *
 * A tabela diz o que PODE ferir. O que **chega** a um cometa é outra coisa e é
 * menor: neste gate estão ligados os projéteis e a área de uma explosão. Um
 * míssil continua a perseguir cascos e uma serra continua a tocar em cascos,
 * porque redirecioná-los seria decidir alvo — uma decisão de desenho que este
 * gate não tem. O dia em que forem ligados, esta tabela já os autoriza e não
 * precisa de mudar.
 *
 * ## Veneno, sangramento e reflexão não estão aqui, e não é omissão
 *
 * Nenhum deles chega a um recurso, e não por uma condição: um veneno é uma
 * pilha guardada NUMA NAVE, uma marca de sangramento também, e uma reflexão é
 * uma resposta de quem levou o tiro. Nenhuma dessas três coisas tem por onde
 * apontar para uma pedra. A impossibilidade é estrutural, que é mais forte do
 * que uma linha a proibi-la.
 */
export const DAMAGE_ORIGIN_MAY_HURT_RESOURCE: Readonly<Record<DamageOrigin, boolean>> =
  Object.freeze({
    cannon: true,
    pet: false,
    jewel: true,
    saw: true,
    temporary_cannon: true,
    card: true,
  });

/** Reads the resource table. The only way THAT question is ever asked. */
export function damageOriginMayHurtResource(origin: DamageOrigin): boolean {
  return DAMAGE_ORIGIN_MAY_HURT_RESOURCE[origin];
}

/* -------------------------------------------------------------------------- */
/*  What a jewel emits                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Every effect a jewel will emit damage or healing through, named now.
 *
 * Named ahead of the mechanics on purpose: the test that proves "a missile
 * cannot set off a missile" has to be able to say `missile`, and saying it by
 * searching the source for the word would prove nothing about what the code
 * does. This list plus {@link JEWEL_EFFECT_EMITS} is what the test walks.
 *
 * It is NOT a registry of ACTIVE effects - nothing here is implemented, and
 * {@link jewelTriggersFor} returns nothing for every origin. It is a statement
 * of what the graph will contain, made while it is still cheap to be
 * exhaustive.
 */
export const JEWEL_EMITTED_EFFECTS = [
  /** Arsenal Ígneo: the extra missile. */
  'missile',
  /** Diamante Serrilhado: one orbiting saw touching one enemy (gate V28G.P). */
  'saw_contact',
  /** Ciclone de Lâminas: the cut on the enemy struck. */
  'cyclone_cut',
  /** Núcleo Atômico: the blast, and everything in its radius. */
  'atomic_blast',
  /** Garra Hemorrágica: the detonation of every mark. */
  'bleed_detonation',
  /** Orbe da Víbora: one periodic tick of the accumulated stacks. */
  'poison_tick',
  /** Égide Espinhosa: the retaliation. */
  'reflection',
  /** Orbe Vampírico: the heal on a landed shot. */
  'lifesteal_heal',
  /** Coração Vital: the support heal a volley confirms (gate V28G.U). */
  'vital_heal',
] as const;

export type JewelEmittedEffect = (typeof JEWEL_EMITTED_EFFECTS)[number];

/**
 * The origin each jewel effect stamps on what it emits.
 *
 * Eight say `jewel` and one says `saw`, and writing the table out anyway is
 * deliberate: a function returning the constant `'jewel'` would be a rule, and a
 * rule can be bypassed by a tenth effect that forgets to call it. A `Record`
 * keyed by the closed list cannot be extended without an entry, and the test
 * asserts no entry is `cannon` - so a future effect that tried to emit one in
 * order to chain would fail a test rather than ship a loop.
 *
 * ## The one that is not `jewel`, and why the loop is still closed
 *
 * `saw_contact` stamps `saw`, because the V28G.P contract lets a confirmed saw
 * contact seed Poison, seed a Bleeding mark, cut with the Cyclone and siphon
 * with Lifesteal. Everything on that list emits `jewel` in turn, and `jewel`
 * sets off nothing - so the graph is a two-step path with no cycle rather than a
 * loop guarded by a counter.
 *
 * What `saw` may set off is a strict subset of what `cannon` may, and it is
 * {@link DAMAGE_ORIGIN_TRIGGERABLE_EFFECTS} that says which - never a condition
 * written inside a simulation.
 */
export const JEWEL_EFFECT_EMITS: Readonly<Record<JewelEmittedEffect, DamageOrigin>> = Object.freeze(
  {
    missile: 'jewel',
    saw_contact: 'saw',
    cyclone_cut: 'jewel',
    atomic_blast: 'jewel',
    bleed_detonation: 'jewel',
    poison_tick: 'jewel',
    reflection: 'jewel',
    lifesteal_heal: 'jewel',
    vital_heal: 'jewel',
  },
);

/* -------------------------------------------------------------------------- */
/*  The dispatch                                                              */
/* -------------------------------------------------------------------------- */

/** Frozen, shared, and allocated once: a blow must not allocate to learn it triggers nothing. */
const NO_TRIGGERS: readonly JewelEmittedEffect[] = Object.freeze([]);

/**
 * The effects that are actually wired into combat right now.
 *
 * **Empty, and that is this gate's whole delivery for the graph.** The contract
 * is decided, the table is closed, the dispatch exists and the damage path
 * calls it - and it returns nothing, because no jewel is active. The first
 * jewel is then a REGISTRATION rather than a new `if` in the damage path.
 */
const ACTIVE_JEWEL_TRIGGERS: readonly JewelEmittedEffect[] = Object.freeze([]);

/**
 * What this blow may set off.
 *
 * The gate and the registry, in one call, so a damage path never asks the two
 * questions separately and never gets them in the wrong order. A `jewel` blow
 * and a `pet` blow return empty **because the table forbids them**; a `cannon`
 * blow returns empty **because nothing is registered yet**. The two reasons are
 * different and {@link damageOriginMayTriggerJewel} is how a caller tells them
 * apart.
 */
export function jewelTriggersFor(origin: DamageOrigin): readonly JewelEmittedEffect[] {
  if (!damageOriginMayTriggerJewel(origin)) {
    return NO_TRIGGERS;
  }
  return ACTIVE_JEWEL_TRIGGERS;
}

/* -------------------------------------------------------------------------- */
/*  **What each origin may set off, effect by effect** (gate V28G.P)          */
/* -------------------------------------------------------------------------- */

/**
 * The list a CANNON may set off. Everything hostile a jewel emits, plus the heal.
 *
 * Derived by exclusion from the closed list rather than written out, so a tenth
 * effect lands here automatically and has to be excluded ON PURPOSE if it does
 * not belong - the opposite of a hand-written list that silently omits it.
 *
 * `reflection` is not on it and cannot be: a reflection is a DEFENSIVE roll made
 * on a payload already in flight, never something an outgoing blow sets off.
 */
const CANNON_TRIGGERS: readonly JewelEmittedEffect[] = Object.freeze(
  JEWEL_EMITTED_EFFECTS.filter((effect) => effect !== 'reflection' && effect !== 'saw_contact'),
);

/**
 * **The list a SAW contact may set off**, and the owner named all four.
 *
 * Poison, Bleeding, the Cyclone and Lifesteal - written as the effects they
 * emit, because this table is keyed by effect and never by jewel id. A saw that
 * named `jewel_poison` would be a mechanic reading a catalogue entry; naming
 * `poison_tick` is naming the thing that happens.
 *
 * ## What is missing, and that it is missing ON PURPOSE
 *
 * `missile` and `atomic_blast`. The owner excluded them by name, and the reason
 * is structural rather than a preference: both are drawn ONCE PER VOLLEY by
 * `volley-lottery.ts`, and a saw does not fire a volley. Excluding them here as
 * well is belt and braces - the call site does not exist, and if somebody wrote
 * one this table would still refuse it.
 *
 * `vital_heal` is missing too, and since gate V28G.U the reason is the same one
 * the Missile and the Atomic Core have rather than "there is no mechanic yet":
 * the Coração Vital rolls exactly ONCE PER VOLLEY, and a saw fires no volley.
 * The owner's contract says it outright - *«não é ativado por Serras, pets,
 * Veneno, Sangramento, Ciclone, Míssil ou Bomba»* - and this row is where the
 * sentence stops depending on there being no caller.
 */
const SAW_TRIGGERS: readonly JewelEmittedEffect[] = Object.freeze([
  'poison_tick',
  'bleed_detonation',
  'cyclone_cut',
  'lifesteal_heal',
] satisfies readonly JewelEmittedEffect[]);

/**
 * **The second closed table: which effects each origin may set off.**
 *
 * ## Why a boolean was no longer enough
 *
 * Because `saw` is the first origin whose answer is "some of them". Before it,
 * every origin was all-or-nothing and {@link DAMAGE_ORIGIN_MAY_TRIGGER_JEWEL}
 * carried the whole contract. A saw may seed poison and may not launch a
 * missile, and squeezing that into one boolean would have meant writing the
 * difference as an `if` inside two simulations - which is exactly the shape this
 * file exists to refuse.
 *
 * `Record<DamageOrigin, readonly JewelEmittedEffect[]>` keeps it closed in the
 * same way the boolean was: a fifth origin without a row is a compile error.
 *
 * The two tables agree by construction and the proof asserts it: an origin with
 * an empty row here must be `false` there, and a non-empty row must be `true`.
 * Two tables that could disagree would be worse than one that says too little.
 */
export const DAMAGE_ORIGIN_TRIGGERABLE_EFFECTS: Readonly<
  Record<DamageOrigin, readonly JewelEmittedEffect[]>
> = Object.freeze({
  cannon: CANNON_TRIGGERS,
  pet: NO_TRIGGERS,
  jewel: NO_TRIGGERS,
  saw: SAW_TRIGGERS,
  /*
   * As MESMAS que o canhão, e por escolha e não por herança.
   *
   * Um disparo primário de armamento temporário é um tiro da nave: ele pode
   * levar uma bomba, um míssil, um corte, um veneno, um sangramento, uma cura.
   * Não pode reflectir - a reflexão é uma resposta a dano RECEBIDO - nem tocar
   * numa serra, que é a outra origem.
   *
   * Escrito como a mesma referência e não como uma segunda lista, para que uma
   * mudança na do canhão não deixe esta para trás em silêncio.
   */
  temporary_cannon: CANNON_TRIGGERS,
  /*
   * NENHUM, e é a linha que torna a anterior verificável.
   *
   * A tabela de cima diz que uma carta não sorteia; esta diz que não há um
   * único efeito que ela pudesse sortear. As duas juntas são a razão de a
   * profundidade de recursão ser exactamente um, e de não haver um contador
   * que alguém tenha de decrementar.
   */
  card: NO_TRIGGERS,
});

/**
 * **Whether this origin may set off THIS effect.** The only way to ask.
 *
 * Both halves in one call, in the right order, so no damage path can consult the
 * boolean and then forget the list. A `jewel` blow is refused by the first half;
 * a `saw` blow asking for `missile` is refused by the second; and the caller
 * does not have to know which - neither produces anything.
 */
export function damageOriginMayTrigger(origin: DamageOrigin, effect: JewelEmittedEffect): boolean {
  if (!damageOriginMayTriggerJewel(origin)) {
    return false;
  }
  return DAMAGE_ORIGIN_TRIGGERABLE_EFFECTS[origin].includes(effect);
}

/* -------------------------------------------------------------------------- */
/*  O canal de APRESENTAÇÃO, que é mais largo do que a loteria                */
/* -------------------------------------------------------------------------- */

/**
 * **Os efeitos que uma cena pode ser INFORMADA de que aconteceram.**
 *
 * ## Porque é uma segunda lista, e não a mesma
 *
 * Porque nem tudo o que uma cena desenha nasce de uma rolagem. O Ciclone é um
 * proc: sai da loteria, ocupa um intervalo e consome entropia. O Roubo de Vida
 * é uma passiva CONTÍNUA - ADR 0054 - que não sorteia coisa nenhuma e acontece
 * em todos os golpes elegíveis.
 *
 * Os dois produzem um facto que a autoridade confirmou e que uma apresentação
 * pode representar; nenhum outro parentesco existe entre eles. Alargar
 * `OutgoingJewelProcId` - a lista da loteria, em `proc-lottery.ts` - para caber o segundo faria a loteria passar a
 * conhecê-lo - e `proc-lottery.test.ts` e `v28gf-proc-math.mjs` recusam
 * exatamente isso.
 *
 * Então há uma lista para QUEM SORTEIA e outra para QUEM É ANUNCIADO, e a
 * primeira é um subconjunto próprio da segunda.
 *
 * ## O que um id destes é, e o que nunca é
 *
 * É o EFEITO, nunca a joia. `lifesteal_heal` diz "houve um sifão", e não diz
 * `jewel_lifesteal` - uma cena que soubesse o nome de uma joia seria uma cena a
 * decorar um perfil, que é o que `jewel-presentation.test.ts` proíbe. É a mesma
 * razão pela qual `cyclone_cut` não se chama `jewel_blade_cut`.
 */
export type JewelEffectEventId = Extract<
  JewelEmittedEffect,
  | 'cyclone_cut'
  | 'lifesteal_heal'
  | 'atomic_blast'
  | 'missile'
  | 'bleed_detonation'
  | 'reflection'
  | 'vital_heal'
>;

export const JEWEL_EFFECT_EVENT_IDS: readonly JewelEffectEventId[] = Object.freeze([
  'atomic_blast',
  'bleed_detonation',
  'cyclone_cut',
  'lifesteal_heal',
  'missile',
  'reflection',
  'vital_heal',
]);

/**
 * O id que uma cura confirmada carrega, nomeado uma vez.
 *
 * Existe como constante e não como literal espalhado porque vários sítios o
 * escrevem - as duas simulações, as duas cenas e as provas - e vários literais
 * são várias coisas que podem divergir sem que nada as compare.
 */
export const LIFESTEAL_HEAL_EFFECT: JewelEffectEventId = 'lifesteal_heal';

/** O id que um corte confirmado carrega, pela mesma razão. */
export const CYCLONE_CUT_EFFECT: JewelEffectEventId = 'cyclone_cut';

/**
 * O id que uma explosão confirmada carrega, pela mesma razão (gate V28G.J).
 *
 * O EFEITO e nunca a joia: uma cena informada de `atomic_blast` sabe que houve
 * uma explosão e onde, e não sabe - nem pode saber - que `jewel_atomic_bomb`
 * existe.
 */
export const ATOMIC_BLAST_EFFECT: JewelEffectEventId = 'atomic_blast';

/**
 * O id que uma EXPLOSÃO DE MÍSSIL confirmada carrega (gate V28G.L).
 *
 * O EFEITO e nunca a joia, como todos os outros: uma cena informada de `missile`
 * sabe que um míssil rebentou e onde, e não sabe - nem pode saber - que
 * `jewel_missile` existe.
 *
 * ## Porque viaja pelo mesmo canal da explosão atómica
 *
 * Porque o FACTO tem a mesma forma: aconteceu num sítio, não sobre um alvo. Uma
 * bomba que detona contra uma pilastra e um míssil que expira a meio da arena
 * são os dois "houve fogo aqui", e um canal de proc - que exige um alvo - teria
 * de inventar um.
 *
 * O que os separa é este id, e é ele que a cena lê para escolher o desenho: a
 * explosão do míssil é compacta, sem onda de choque e sem círculo de área. O
 * raio MECÂNICO que o evento carrega é **zero** para um míssil, e isso não é uma
 * lacuna - é a verdade: ele não tem dano em área nenhum.
 */
export const MISSILE_EFFECT: JewelEffectEventId = 'missile';

/**
 * O id que uma DETONAÇÃO DE SANGRAMENTO confirmada carrega (gate V28G.N).
 *
 * O EFEITO e nunca a joia, como todos os outros: uma cena informada de
 * `bleed_detonation` sabe que marcas rebentaram, sobre quem e quantas, e não
 * sabe - nem pode saber - que `jewel_bleeding` existe.
 *
 * ## Porque um canal próprio, e não o do proc nem o da explosão
 *
 * Porque a FORMA do facto é outra. Um proc deduplica pelo projétil que o trouxe,
 * e uma detonação não tem projétil - a marca sobreviveu ao tiro que a semeou. Uma
 * explosão tem um centro e um raio, e uma detonação não tem nem um nem outro: ela
 * acontece em vários alvos ao mesmo tempo, cada um com a sua contagem, por uma
 * ORDEM que o jogador deu.
 *
 * A chave de deduplicação é `(autor, tique)`: um autor detona no máximo uma vez
 * por tique, por construção.
 */
export const BLEED_DETONATION_EFFECT: JewelEffectEventId = 'bleed_detonation';

/**
 * O id que uma REFLEXÃO confirmada carrega (gate V28G.O).
 *
 * O EFEITO e nunca a joia, como todos os outros: uma cena informada de
 * `reflection` sabe que um payload hostil foi intercetado e para onde foi
 * redirecionado, e não sabe - nem pode saber - que `jewel_damage_reflection`
 * existe.
 *
 * ## Porque um canal próprio, e não o do proc, o da explosão nem o da detonação
 *
 * Porque a FORMA do facto é outra, e as três alternativas falham por razões
 * diferentes. Um `proc` deduplica pelo projétil que o trouxe, e uma reflexão
 * pode intercetar uma parcela de veneno que nenhum projétil trouxe. Um `blast`
 * tem um centro e um raio, e uma reflexão não tem área nenhuma. Uma detonação
 * acontece em vários alvos por uma ordem, e uma reflexão tem exatamente dois
 * intervenientes: quem intercetou e quem recebe.
 *
 * E há uma coisa que nenhum dos três carrega e esta precisa: **duas posições**.
 * O brilho nasce no refletor e o feixe termina no atacante, e um evento com um
 * ponto só obrigaria a cena a ir buscar o outro ao estado - que num frame
 * publicado depois já não é o mesmo.
 */
export const REFLECTION_EFFECT: JewelEffectEventId = 'reflection';

/**
 * O id que uma CURA DE SUPORTE confirmada carrega (gate V28G.U).
 *
 * O EFEITO e nunca a joia, como todos os outros: uma cena informada de
 * `vital_heal` sabe que alguém curou alguém e quanto, e não sabe - nem pode
 * saber - que `jewel_regeneration` existe.
 *
 * ## Porque viaja pelo canal do PROC, e não por um seu
 *
 * Porque a FORMA do facto é exatamente a de um proc, e é a mesma que o Roubo de
 * Vida já usa: aconteceu SOBRE alguém, tem um autor, tem uma magnitude de cura, e
 * tem uma salva a deduplicá-lo. Os quatro campos que o canal já carrega -
 * `participantId`, `targetId`, `healedMilli` e `projectileId` - são exatamente os
 * quatro que este facto tem, e um canal novo seria protocolo decorativo.
 *
 * O que os separa é o SENTIDO, e ele está nos campos que já existem: no Roubo de
 * Vida o curado é o AUTOR e o alvo é quem foi drenado; aqui o autor é quem ativou
 * e o alvo é quem foi CURADO. Uma apresentação que leia os dois ids sabe, sem
 * mais nada, de onde o feixe parte e onde ele acaba.
 */
export const VITAL_HEAL_EFFECT: JewelEffectEventId = 'vital_heal';

/** Reconhece um id de efeito ANUNCIÁVEL. A única forma de perguntar. */
export function isJewelEffectEventId(value: string): value is JewelEffectEventId {
  return (JEWEL_EFFECT_EVENT_IDS as readonly string[]).includes(value);
}
